<script setup lang="ts">
import { PlusIcon } from "@voltalis/ui-kit/icons";
import { computed, onMounted, ref, watch } from "vue";

import {
  IDeleteProgramCommand,
  useDeleteSiteProgramMutation,
} from "@/composables/kili/internal/mutations/use-delete-site-program-mutation";
import {
  TDuplicateProgramCommand,
  useDuplicateProgramMutation,
} from "@/composables/kili/internal/mutations/use-duplicate-program-mutation";
import { useFetchSiteInstalledApplianceCategories } from "@/composables/kili/sites/fetches/use-fetch-site-installed-appliance-categories";
import { useGetSiteProgramsQuery } from "@/composables/kili/sites/queries/use-get-site-programs-query";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { useToaster } from "@/composables/layout/use-toaster";
import { useBrandedNumberRouteParams } from "@/composables/use-route-params/use-hospitality-number-route-param";
import { useSearchFilter } from "@/composables/use-search-filter";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES } from "@/data/api/kili/programs/program.type";
import { V4HSiteId } from "@/data/common/ids";
import { SiteProgramOverview } from "@/domains/v4b/program/program.types";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import Divider from "primevue/divider";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Skeleton from "primevue/skeleton";

import { TCustomBreadcrumbRouteFormatter } from "@/components/layout/base-layout-breadcrumb.vue";
import BaseLayout from "@/components/layout/base-layout.vue";
import NewProgramOverviewCard from "@/components/programming/new-program-overview-card.vue";
import ProgrammingDuplicatingDialog from "@/components/programming/programming-duplicating-dialog.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const { brandedNumberRouteParam: siteId } = useBrandedNumberRouteParams<V4HSiteId>("siteId");
const { t, k } = useTypedIntl();
const toast = useToaster();

const { data: programs, isLoading } = useGetSiteProgramsQuery(siteId);
const { data: sites } = useGetSitesQuery();

const { mutateAsync: deleteProgram, isPending: isDeletingProgram } = useDeleteSiteProgramMutation();
const { mutateAsync: duplicateProgram, isPending: isDuplicatingProgram } = useDuplicateProgramMutation(
  DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES,
);
const { fetch: fetchInstalledApplianceCategories } = useFetchSiteInstalledApplianceCategories();

const isProgrammingDuplicatingDialogVisible = ref<boolean>(false);

const copiedProgram = ref<SiteProgramOverview>();

const breadcrumbFormatter = computed<TCustomBreadcrumbRouteFormatter>(() => {
  return {
    [ERouteName.PROGRAMMING_LIST_NEW]: () => {
      return siteDetails.value?.name ?? t(k.components.layout.breadcrumb["new-programming-list"]);
    },
  };
});

const defaultProgram = computed<SiteProgramOverview | undefined>(() => {
  return programs.value?.find((program) => program.isSiteDefault);
});

const customPrograms = computed<SiteProgramOverview[]>(() => {
  return programs.value?.filter((program) => !program.isSiteDefault) ?? [];
});

const siteDetails = computed(() => {
  return sites.value?.find((site) => site.siteId === siteId.value);
});

const { searchInput: searchProgramInput, filteredList: filteredCustomPrograms } = useSearchFilter<SiteProgramOverview>(
  customPrograms,
  "name",
);

const isAnyProgramMutationPending = computed(() => isDeletingProgram.value || isDuplicatingProgram.value);

watch(siteId, () => {
  isProgrammingDuplicatingDialogVisible.value = false;
  copiedProgram.value = undefined;
});

async function handleOpenProgramCreation() {
  if (isAnyProgramMutationPending.value) return;

  MixpanelService.trackEvent(EMixpanelEvent.CREATE_PROGRAM);

  await router.push({
    name: ERouteName.PROGRAMMING_CREATION_NEW,
    params: { siteId: siteId.value },
  });
}

async function handleDeleteProgram(command: IDeleteProgramCommand) {
  if (isAnyProgramMutationPending.value) return;

  try {
    await deleteProgram(command);

    toast.add({
      severity: "success",
      detail: t(k.control.programming.deletion.success),
    });

    MixpanelService.trackEvent(EMixpanelEvent.PROGRAM_DELETED);
  } catch (e) {
    toastKiliError(e, keyOf(k.control.programming.deletion.error));
  }
}

function handleOpenDuplicateProgramDialog(program: SiteProgramOverview) {
  copiedProgram.value = program;
  isProgrammingDuplicatingDialogVisible.value = true;
}

async function handleDuplicateProgram(command: TDuplicateProgramCommand) {
  if (isAnyProgramMutationPending.value) return;

  try {
    await duplicateProgram(command);

    isProgrammingDuplicatingDialogVisible.value = false;

    toast.add({
      severity: "success",
      detail: t(k.control.programming.copyToOtherSite.success),
    });

    MixpanelService.trackEvent(EMixpanelEvent.PROGRAM_COPIED, {
      name: command.copiedSiteProgram.name,
    });
  } catch (e) {
    toastKiliError(e, keyOf(k.control.programming.copyToOtherSite.error));
  }
}

async function handleCloneProgram(program: SiteProgramOverview) {
  await handleDuplicateProgram({
    name: t(k.global.misc.copyOf({ name: program.name })),
    siteId: siteId.value,
    copiedSiteProgram: {
      siteId: siteId.value,
      programId: program.id,
      name: t(k.global.misc.copyOf({ name: program.name })),
    },
  });
}

onMounted(async () => {
  // prefetch installed appliance categories on the site
  await fetchInstalledApplianceCategories(siteId.value);
});
</script>

<template>
  <BaseLayout
    :breadcrumb-formatter="breadcrumbFormatter"
    class="flex flex-col gap-8"
  >
    <template #breadcrumbRight>
      <HospitalityButton
        data-test-id="create-program-button"
        :icon="PlusIcon"
        :label="t(k.control.programming.newProgram)"
        @click="handleOpenProgramCreation"
      />
    </template>

    <div class="flex flex-col gap-[6px]">
      <div
        v-if="!isLoading"
        class="flex row-dot-divider"
      >
        <h4>
          {{ t(k.control.programming.programList) }}
        </h4>

        <h4>
          {{ siteDetails?.name ?? t(k.control.programming.subtitle) }}
        </h4>
      </div>

      <Skeleton
        v-else
        class="!w-40 !h-8"
      />

      <span class="text-text-secondary">
        {{ t(k.control.programming.customProgramsCount({ count: customPrograms.length })) }}
      </span>
    </div>

    <div class="flex flex-col gap-6 h-full w-full">
      <div class="flex gap-4 w-full">
        <IconField class="w-full">
          <InputIcon class="pi pi-search" />
          <InputText
            v-model.trim="searchProgramInput"
            class="w-full"
            :placeholder="t(k.control.programming.searchProgram)"
          />
        </IconField>
      </div>

      <div class="flex flex-col gap-3 w-full h-full">
        <section
          v-if="defaultProgram"
          class="pb-2 flex flex-col gap-[18px]"
          data-test-id="default-program-section"
        >
          <h3 class="text-xs uppercase text-gray-400 font-medium">
            {{ t(k.control.programming.defaultProgram) }}
          </h3>

          <NewProgramOverviewCard
            :can-delete="false"
            is-default-program
            :program="defaultProgram"
            :site-id="siteId"
            @duplicate="handleOpenDuplicateProgramDialog($event)"
            @select="router.push({ name: ERouteName.PROGRAMMING_DETAILS_NEW, params: { siteId, programId: $event } })"
          />

          <Divider class="!m-0" />
        </section>

        <section
          class="flex flex-col gap-3 flex-grow overflow-y-scroll basis-0 pb-4"
          data-test-id="custom-programs-section"
        >
          <NewProgramOverviewCard
            v-for="program in filteredCustomPrograms"
            :key="program.id"
            :program="program"
            :site-id="siteId"
            @clone="handleCloneProgram(program)"
            @delete="handleDeleteProgram($event)"
            @duplicate="handleOpenDuplicateProgramDialog($event)"
            @select="router.push({ name: ERouteName.PROGRAMMING_DETAILS_NEW, params: { siteId, programId: $event } })"
          />
        </section>
      </div>
    </div>
  </BaseLayout>

  <ProgrammingDuplicatingDialog
    v-model="isProgrammingDuplicatingDialogVisible"
    :duplicated-program="copiedProgram"
    :is-pending="isDuplicatingProgram"
    :sites="sites"
    @submit="handleDuplicateProgram($event)"
  />
</template>
